import React, { useState } from "react";
import { Folder, Pin, File as FileIcon, MoreVertical, ExternalLink, Trash2, Edit2, Copy, PinOff, Loader2 } from "lucide-react";
import { UIRoomItem } from "../../../models/RoomItem";
import { Util } from "../../../services/Utils/Util";
import { UtilApp } from "../../../services/Utils/UtilApp";
import { My } from "../../../services/My";
import { Checkbox } from "../../../components/ui/checkbox";
import { cn } from "../../../lib/utils";
import { Fire } from "../../../services/FireClient";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../../../components/ui/dropdown-menu";
import { Trans, t } from '@lingui/macro';
import { useToast } from "../../../components/ui/use-toast";
import { RoomItem } from "../../../models/RoomItem";

interface FileListItemProps {
    uitem: UIRoomItem;
    viewMode: 'list' | 'grid';
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    onMoveFolder?: () => void;
    onPreview?: (url: string) => void;
    canEdit?: boolean;
    isMobile?: boolean;
    onDelete?: () => void;
}

// 파일 확장자 추출 (최대 4글자)
function getFileExtension(filename: string | undefined): string {
    if (!filename) return '';
    const parts = filename.split('.');
    if (parts.length < 2) return '';
    const ext = parts[parts.length - 1].toUpperCase();
    return ext.length <= 4 ? ext : ext.substring(0, 4);
}

// 확장자별 배경색
function getExtensionColor(ext: string): string {
    const colors: { [key: string]: string } = {
        'PDF': 'bg-red-500',
        'DOC': 'bg-blue-500',
        'DOCX': 'bg-blue-500',
        'XLS': 'bg-green-600',
        'XLSX': 'bg-green-600',
        'PPT': 'bg-orange-500',
        'PPTX': 'bg-orange-500',
        'ZIP': 'bg-yellow-600',
        'RAR': 'bg-yellow-600',
        'TXT': 'bg-gray-500',
        'CSV': 'bg-green-500',
        'MP3': 'bg-purple-500',
        'MP4': 'bg-pink-500',
        'MOV': 'bg-pink-500',
    };
    return colors[ext] || 'bg-gray-400';
}

// 파일 아이콘 컴포넌트
const FileIconWithExt: React.FC<{ filename?: string; size?: 'sm' | 'md' }> = ({ filename, size = 'md' }) => {
    const ext = getFileExtension(filename);
    const bgColor = getExtensionColor(ext);
    const iconSize = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
    const textSize = size === 'sm' ? 'text-[8px]' : 'text-[9px]';

    return (
        <div className={cn("relative flex items-center justify-center rounded bg-gray-100", iconSize)}>
            <FileIcon className={cn("text-gray-400", size === 'sm' ? 'w-5 h-5' : 'w-6 h-6')} />
            {ext && (
                <span className={cn(
                    "absolute bottom-0 left-0 right-0 text-center text-white font-bold rounded-b px-0.5",
                    textSize, bgColor
                )}>
                    {ext}
                </span>
            )}
        </div>
    );
};

// UUID 생성
function uuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export const FileListItem: React.FC<FileListItemProps> = ({
    uitem, checked, onCheckedChange, onMoveFolder, onPreview, canEdit, isMobile = false, onDelete
}) => {
    const { toast } = useToast();
    const item = uitem.item;
    const isFolder = item.type === 'folder';
    const isMessageFolder = (uitem.itemType as string) === 'MessageFolder';
    const isImage = item.url && Util.isImageUrl(item.url);
    const [isOpening, setIsOpening] = useState(false);

    const handleOpen = async () => {
        if (isFolder) {
            onMoveFolder?.();
            return;
        }

        setIsOpening(true);
        try {
            const res = await Fire.item.getRoomItemDownloadURL(item.roomId, item.id);
            if (res.status === 'SUCCESS' && res.url) {
                // Server sets responseDisposition: attachment, so this will trigger download
                window.open(res.url, '_blank');
            } else {
                toast({
                    variant: "destructive",
                    title: t`Download Failed`,
                    description: res.status === 'NOT_MEMBER' ? t`You don't have permission.` : t`File not found or expired.`,
                });
            }
        } catch (error) {
            console.error("Failed to get download URL", error);
            toast({
                variant: "destructive",
                title: t`Error`,
                description: (error as Error).message,
            });
        } finally {
            setIsOpening(false);
        }
    };

    // PC/Tablet: 테이블 행 형식
    if (!isMobile) {
        return (
            <div className="flex items-center gap-3 px-3 py-2 bg-white rounded-[8px] hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all group mb-1">
                <Checkbox
                    checked={checked}
                    onCheckedChange={onCheckedChange}
                    disabled={isMessageFolder}
                    className={cn("w-4 h-4 shrink-0", isMessageFolder && "opacity-20")}
                />

                <div
                    className="cursor-pointer shrink-0 relative"
                    onClick={handleOpen}
                >
                    {isFolder ? (
                        <div className="w-8 h-8 flex items-center justify-center rounded bg-gray-50 text-orange-400">
                            <Folder className="w-5 h-5 fill-current" />
                        </div>
                    ) : isImage ? (
                        <div className="w-8 h-8 rounded overflow-hidden bg-gray-50">
                            <img src={item.url} className="w-full h-full object-cover" alt={item.title} />
                        </div>
                    ) : (
                        <FileIconWithExt filename={item.title || item.file?.filename} size="sm" />
                    )}
                    {isOpening && (
                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded">
                            <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                        </div>
                    )}
                </div>

                {/* 파일명 */}
                <div
                    className="flex-[4] min-w-0 font-medium text-gray-900 truncate cursor-pointer hover:text-orange-600 text-sm pl-1"
                    onClick={handleOpen}
                >
                    {item.title || item.url}
                </div>

                {/* 사용자 */}
                {!isMessageFolder && (
                    <div className="flex-[2] min-w-0 flex flex-col justify-center">
                        <div className="text-center text-sm text-gray-600 truncate">
                            {item.user.name}
                        </div>
                        {item.user.email && (
                            <div className="text-center text-xs text-gray-400 truncate">
                                {item.user.email}
                            </div>
                        )}
                    </div>
                )}

                {/* 날짜 */}
                {!isMessageFolder && (
                    <div className="flex-[2] min-w-0 text-center text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
                        {UtilApp.dateStrToDay(item.created, My.user.lang)}
                    </div>
                )}

                {/* 크기 */}
                {!isMessageFolder && (
                    <div className="flex-[1] min-w-0 text-right text-sm text-gray-500 truncate">
                        {item.file ? Util.niceBytes(item.file.sizeByte) : ''}
                    </div>
                )}

                {/* 액션 (핀 + 메뉴) */}
                <div className="w-12 flex items-center justify-end gap-2 shrink-0">
                    {/* 핀 아이콘 */}
                    {item.pinned && <Pin className="w-4 h-4 text-red-500 shrink-0" />}

                    {/* 메뉴 */}
                    {!isMessageFolder && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <div
                                    className="p-1.5 rounded-full hover:bg-gray-100 cursor-pointer text-gray-400 hover:text-gray-900 transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <MoreVertical className="w-4 h-4" />
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {/* Open Link */}
                                {item.type !== 'folder' && (
                                    <DropdownMenuItem onClick={handleOpen} className="gap-2" disabled={isOpening}>
                                        {isOpening ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                                        <span><Trans>Open new tab</Trans></span>
                                    </DropdownMenuItem>
                                )}

                                {/* Copy to My Room */}
                                {item.roomId !== My.user.id && (
                                    <DropdownMenuItem onClick={async () => {
                                        let copy = Util.pojo(item) as RoomItem;
                                        copy.id = uuid();
                                        copy.roomId = My.user.id;
                                        copy.pinned = false;
                                        copy.created = new Date();
                                        await Fire.item.saveRoomItem(copy);
                                        toast({ title: "Copied to My Room" });
                                    }} className="gap-2">
                                        <Copy className="w-4 h-4" />
                                        <span><Trans>Copy to My Room</Trans></span>
                                    </DropdownMenuItem>
                                )}

                                {canEdit && (
                                    <>
                                        <DropdownMenuSeparator />
                                        {/* Edit */}
                                        <DropdownMenuItem onClick={() => {
                                            const newTitle = prompt("Edit title", item.title);
                                            if (newTitle && newTitle !== item.title) {
                                                item.title = newTitle;
                                                Fire.item.saveRoomItem(item);
                                            }
                                        }} className="gap-2">
                                            <Edit2 className="w-4 h-4" />
                                            <span><Trans>Edit</Trans></span>
                                        </DropdownMenuItem>

                                        {/* Pin */}
                                        <DropdownMenuItem onClick={() => {
                                            item.pinned = !item.pinned;
                                            Fire.item.saveRoomItem(item);
                                        }} className="gap-2">
                                            {item.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                                            <span>{item.pinned ? <Trans>Unpin</Trans> : <Trans>Pin</Trans>}</span>
                                        </DropdownMenuItem>

                                        {/* Delete */}
                                        <DropdownMenuItem onClick={() => {
                                            onDelete?.();
                                        }} className="gap-2 text-red-600 focus:text-red-600 focus:bg-red-50">
                                            <Trash2 className="w-4 h-4" />
                                            <span><Trans>Delete</Trans></span>
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
        );
    }

    // Mobile: 카드 형식 (2줄)
    return (
        <div className="flex items-center gap-3 py-3 border-b border-gray-100 bg-white transition-all group">
            <div className="">
                <Checkbox
                    checked={checked}
                    onCheckedChange={onCheckedChange}
                    disabled={isMessageFolder}
                    className={cn(isMessageFolder && "opacity-20")}
                />
            </div>

            <div className="flex-1 min-w-0 flex flex-col gap-2">
                <div className="flex items-center gap-3">
                    <div
                        className="cursor-pointer shrink-0 relative"
                        onClick={handleOpen}
                    >
                        {isFolder ? (
                            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-50 text-orange-400">
                                <Folder className="w-6 h-6 fill-current" />
                            </div>
                        ) : isImage ? (
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-50">
                                <img src={item.url} className="w-full h-full object-cover" alt={item.title} />
                            </div>
                        ) : (
                            <FileIconWithExt filename={item.title || item.file?.filename} size="md" />
                        )}
                        {isOpening && (
                            <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg">
                                <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                            </div>
                        )}
                    </div>

                    <div
                        className="flex-1 font-medium text-gray-900 truncate cursor-pointer hover:text-orange-600"
                        onClick={handleOpen}
                    >
                        {item.title || item.url}
                    </div>
                </div>

                {!isMessageFolder && (
                    <div className="flex items-center gap-2 text-[11px] text-gray-400">
                        <span className="font-semibold text-gray-600">{item.user.name}</span>
                        <span className="opacity-50">•</span>
                        <span>{UtilApp.dateStrToDay(item.created, My.user.lang)}</span>
                        {item.file && (
                            <>
                                <span className="opacity-50">•</span>
                                <span>{Util.niceBytes(item.file.sizeByte)}</span>
                            </>
                        )}
                        {item.pinned && <Pin className="w-3 h-3 text-orange-500 fill-current" />}
                    </div>
                )}
            </div>

            {!isMessageFolder && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <div
                            className="p-2 -mr-2 rounded-full active:bg-gray-100"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MoreVertical className="w-5 h-5 text-gray-400" />
                        </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {/* Open Link */}
                        {item.type !== 'folder' && (
                            <DropdownMenuItem onClick={handleOpen} className="gap-2" disabled={isOpening}>
                                {isOpening ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                                <span><Trans>Open new tab</Trans></span>
                            </DropdownMenuItem>
                        )}

                        {/* Copy to My Room */}
                        {item.roomId !== My.user.id && (
                            <DropdownMenuItem onClick={async () => {
                                let copy = Util.pojo(item) as RoomItem;
                                copy.id = uuid();
                                copy.roomId = My.user.id;
                                copy.pinned = false;
                                copy.created = new Date();
                                await Fire.item.saveRoomItem(copy);
                                toast({ title: "Copied to My Room" });
                            }} className="gap-2">
                                <Copy className="w-4 h-4" />
                                <span><Trans>Copy to My Room</Trans></span>
                            </DropdownMenuItem>
                        )}

                        {canEdit && (
                            <>
                                <DropdownMenuSeparator />
                                {/* Edit */}
                                <DropdownMenuItem onClick={() => {
                                    const newTitle = prompt("Edit title", item.title);
                                    if (newTitle && newTitle !== item.title) {
                                        item.title = newTitle;
                                        Fire.item.saveRoomItem(item);
                                    }
                                }} className="gap-2">
                                    <Edit2 className="w-4 h-4" />
                                    <span><Trans>Edit</Trans></span>
                                </DropdownMenuItem>

                                {/* Pin */}
                                <DropdownMenuItem onClick={() => {
                                    item.pinned = !item.pinned;
                                    Fire.item.saveRoomItem(item);
                                }} className="gap-2">
                                    {item.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                                    <span>{item.pinned ? <Trans>Unpin</Trans> : <Trans>Pin</Trans>}</span>
                                </DropdownMenuItem>

                                {/* Delete */}
                                <DropdownMenuItem onClick={() => {
                                    onDelete?.();
                                }} className="gap-2 text-red-600 focus:text-red-600 focus:bg-red-50">
                                    <Trash2 className="w-4 h-4" />
                                    <span><Trans>Delete</Trans></span>
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );
};
